export function validateFlowSchema(schema) {
  const errors = [];
  if (!schema || !Array.isArray(schema.steps)) {
    throw new Error("Flow schema invalid: missing steps array");
  }
  const ids = new Set();
  const idToStep = {};
  schema.steps.forEach((s) => {
    if (!s.id) errors.push("Step missing id");
    if (ids.has(s.id)) errors.push(`Duplicate step id: ${s.id}`);
    ids.add(s.id);
    idToStep[s.id] = s;
    // Require clear journey markers for maintainability
    // Text steps can optionally omit promptKey if they only show input fields
    if (!s.promptKey && s.type !== "system" && s.type !== "router" && s.type !== "text") {
      errors.push(`Step ${s.id} missing promptKey`);
    }
  });
  // Helper to assert next references
  const checkNext = (next, ctx) => {
    if (!next) {
      errors.push(`Missing next for ${ctx}; every interactive path must lead somewhere. Use next: "end" to explicitly end.`);
      return;
    }
    if (typeof next === "string" && next !== "end" && !idToStep[next]) {
      errors.push(`Unknown next id "${next}" referenced from ${ctx}`);
    }
  };
  // Validate per-type constraints
  schema.steps.forEach((s) => {
    if (s.type === "singleChoice") {
      if (typeof s.options === "function") {
        // Function-based options - validate by calling with empty answers
        try {
          const resolvedOptions = s.options({});
          if (!Array.isArray(resolvedOptions) || resolvedOptions.length === 0) {
            errors.push(`singleChoice step ${s.id} options function must return non-empty array`);
          } else {
            resolvedOptions.forEach((opt) => {
              if (!opt.id) errors.push(`Option missing id in step ${s.id}`);
              if (!opt.labelKey && !opt.label) errors.push(`Option ${opt.id} in step ${s.id} missing labelKey or label`);
              // Require next at option or step level
              if (!opt.next && !s.next) {
                errors.push(`Option ${opt.id} in step ${s.id} missing next (and step.next not provided)`);
              }
              if (opt.next) checkNext(opt.next, `option ${opt.id} in step ${s.id}`);
            });
          }
        } catch (e) {
          errors.push(`singleChoice step ${s.id} options function threw error: ${e.message}`);
        }
      } else {
        if (!Array.isArray(s.options) || s.options.length === 0) {
          errors.push(`singleChoice step ${s.id} must have non-empty options`);
        }
        s.options?.forEach((opt) => {
          if (!opt.id) errors.push(`Option missing id in step ${s.id}`);
          if (!opt.labelKey) errors.push(`Option ${opt.id} in step ${s.id} missing labelKey`);
          // Require next at option or step level
          if (!opt.next && !s.next) {
            errors.push(`Option ${opt.id} in step ${s.id} missing next (and step.next not provided)`);
          }
          if (opt.next) checkNext(opt.next, `option ${opt.id} in step ${s.id}`);
        });
      }
      if (typeof s.next === "string") checkNext(s.next, `step ${s.id}`);
    } else if (s.type === "multiChoice") {
      if (!s.answerKey) errors.push(`multiChoice step ${s.id} must specify answerKey`);
      if (typeof s.options === "function") {
        // Function-based options - validate by calling with empty answers
        try {
          const resolvedOptions = s.options({});
          if (!Array.isArray(resolvedOptions)) {
            errors.push(`multiChoice step ${s.id} options function must return an array`);
          } else if (resolvedOptions.length === 0) {
            // Allow empty array for function-based options (e.g., when city is "other")
          } else {
            resolvedOptions.forEach((opt) => {
              if (!opt.id) errors.push(`Option missing id in step ${s.id}`);
              if (!opt.labelKey && !opt.label) errors.push(`Option ${opt.id} in step ${s.id} missing labelKey or label`);
              if (opt.isOther && !opt.otherPlaceholderKey) {
                errors.push(`Option ${opt.id} in step ${s.id} marked isOther must provide otherPlaceholderKey`);
              }
            });
          }
        } catch (e) {
          errors.push(`multiChoice step ${s.id} options function threw error: ${e.message}`);
        }
      } else {
        if (!Array.isArray(s.options) || s.options.length === 0) {
          errors.push(`multiChoice step ${s.id} must have non-empty options`);
        }
        s.options?.forEach((opt) => {
          if (!opt.id) errors.push(`Option missing id in step ${s.id}`);
          if (!opt.labelKey) errors.push(`Option ${opt.id} in step ${s.id} missing labelKey`);
          if (opt.isOther && !opt.otherPlaceholderKey) {
            errors.push(`Option ${opt.id} in step ${s.id} marked isOther must provide otherPlaceholderKey`);
          }
        });
      }
      if (!s.next) errors.push(`multiChoice step ${s.id} missing next`);
      if (typeof s.next === "string") checkNext(s.next, `step ${s.id}`);
    } else if (s.type === "text") {
      if (!s.answerKey) errors.push(`text step ${s.id} must specify answerKey`);
      if (!Array.isArray(s.fields) || s.fields.length === 0) {
        errors.push(`text step ${s.id} must have fields`);
      }
      if (!s.next) errors.push(`text step ${s.id} missing next`);
      if (typeof s.next === "string") checkNext(s.next, `step ${s.id}`);
    } else if (s.type === "system") {
      // system may auto-advance; if provided, ensure next is valid
      if (typeof s.next === "string") checkNext(s.next, `step ${s.id}`);
    }
  });
  if (errors.length) {
    const e = new Error(`Flow schema validation failed:\n- ${errors.join("\n- ")}`);
    // Throw to fail fast in dev/build
    throw e;
  }
  return true;
}


